import { useState, useEffect, useCallback } from 'react';
import { Loader2, Sparkles, Plus, Trash2, Lock } from 'lucide-react';
import LayerHeader from '../components/ui/LayerHeader';
import { NumCell, CELL } from '../components/ui/NumCell';
import { TH, makeSticky } from '../components/ui/gridTable';
import Dropdown from '../components/receptura/Dropdown';
import { num, fmtKc, MESICE_SHORT, MKEYS } from '../lib/formatters';
import { ACCENT, W_MESIC, tableSurfaces, OK, VIOLET, WARN, BAD } from '../lib/uiTokens';
import { AopTable, BtnUlozit, LayerSpinner } from '../components/ui/aopUi';
import useSirokyDisplej from '../hooks/useSirokyDisplej';

const MRE = /^m(\d{2})$/;
const zamSet = (r) => (Array.isArray(r.mesice_zamcene) ? r.mesice_zamcene : []);

// Šířky sloupců + offsety pro sticky (fixní) levý blok Kanál → Roční cíl
const W = { kanal: 168, sazba: 76, lonsko: 138, proc: 66, cil: 152, mesic: W_MESIC, koment: 200, akce: 44 };
const LEFT = { kanal: 0, sazba: W.kanal, lonsko: W.kanal + W.sazba, proc: W.kanal + W.sazba + W.lonsko, cil: W.kanal + W.sazba + W.lonsko + W.proc };

export default function FinanceAopPrijmy({ rok, variantaId, cardStyle, isDark }) {
    const siroky = useSirokyDisplej();
    const [rows, setRows] = useState([]);
    const [dirty, setDirty] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generujici, setGenerujici] = useState(false);
    const [defaultProcento, setDefaultProcento] = useState(5);

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token };

    const qVar = variantaId ? `&varianta_id=${variantaId}` : '';

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetch(`/api/finance/aop/prijmy?rok=${rok}${qVar}`).then(r => r.json());
            setRows(Array.isArray(data) ? data : []);
            setDirty(new Set());
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rok, variantaId]);

    useEffect(() => { loadData(); }, [loadData]);

    const updateCell = (id, field, value) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const next = { ...r, [field]: value };
            // Ruční editace měsíce → automaticky ho zamkni (generátor ani % přepočet ho nepřepíše)
            const mm = MRE.exec(field);
            if (mm) {
                const mnum = parseInt(mm[1], 10);
                const zam = zamSet(r);
                if (!zam.includes(mnum)) next.mesice_zamcene = [...zam, mnum].sort((a, b) => a - b);
            }
            // Fixní kanály: % je vstup → realtime přepočet ODEMČENÝCH měsíců ze základu
            // (loňsko) se zachováním sezónního tvaru. Zamčené měsíce zůstávají beze změny.
            if (field === 'procento_navyseni' && r.je_fixni) {
                const zam = zamSet(r);
                const cil = num(next.zaklad_loni) * (1 + num(value) / 100);
                const sum = MKEYS.reduce((s, k) => s + num(r[k]), 0);
                MKEYS.forEach((k, idx) => {
                    if (zam.includes(idx + 1)) return; // zamčený měsíc — neměnit
                    next[k] = sum > 0
                        ? Math.round(cil * (num(r[k]) / sum) * 100) / 100
                        : Math.round((cil / 12) * 100) / 100;
                });
            }
            return next;
        }));
        setDirty(prev => new Set(prev).add(id));
    };

    // Ruční přepnutí zámku měsíce (odemčení vrátí buňku pod automatiku % / generátor)
    const toggleMesicLock = (id, mnum) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const zam = zamSet(r);
            const next = zam.includes(mnum) ? zam.filter(x => x !== mnum) : [...zam, mnum].sort((a, b) => a - b);
            return { ...r, mesice_zamcene: next };
        }));
        setDirty(prev => new Set(prev).add(id));
    };

    const handleGenerovat = async () => {
        if (!confirm(`Vygenerovat příjmy ${rok} z loňských tržeb (${rok - 1})? Přepíše hodnoty fixních kanálů; ručně zamčené měsíce zůstanou.`)) return;
        setGenerujici(true);
        try {
            const res = await fetch('/api/finance/aop/prijmy/generovat', {
                method: 'POST', headers,
                body: JSON.stringify({ rok, varianta_id: variantaId, default_procento: num(defaultProcento) }),
            }).then(r => r.json());
            setRows(res.radky || []);
            setDirty(new Set());
        } catch (e) { alert('Chyba při generování'); }
        setGenerujici(false);
    };

    const handlePridatKanal = async () => {
        try {
            const created = await fetch('/api/finance/aop/prijmy', {
                method: 'POST', headers,
                body: JSON.stringify({ rok, varianta_id: variantaId, nazev: 'Nový kanál', dph_sazba: 21 }),
            }).then(r => r.json());
            // vloží lokálně (seřazeno dle pořadí), nezahodí rozdělané úpravy
            setRows(prev => [...prev, created].sort((a, b) => (a.poradi - b.poradi) || (a.id - b.id)));
        } catch (e) { alert('Chyba'); }
    };

    const handleSmazat = async (id) => {
        if (!confirm('Smazat tento kanál?')) return;
        try {
            const r = await fetch(`/api/finance/aop/prijmy/${id}`, { method: 'DELETE', headers });
            if (!r.ok) { alert('Tento kanál nelze smazat.'); return; }
            setRows(prev => prev.filter(x => x.id !== id));
            setDirty(prev => { const n = new Set(prev); n.delete(id); return n; });
        } catch (e) { alert('Chyba'); }
    };

    const handleUlozit = async () => {
        if (dirty.size === 0) return;
        setSaving(true);
        try {
            await Promise.all([...dirty].map(id => {
                const r = rows.find(x => x.id === id);
                if (!r) return null;
                const body = {
                    nazev: r.nazev,
                    dph_sazba: Number(r.dph_sazba),
                    zaklad_loni: num(r.zaklad_loni),
                    procento_navyseni: num(r.procento_navyseni),
                    rocni_cil: MKEYS.reduce((s, k) => s + num(r[k]), 0), // = součet měsíců
                    poznamka: r.poznamka || null,
                    mesice_zamcene: zamSet(r),
                    ...Object.fromEntries(MKEYS.map(k => [k, num(r[k])])),
                };
                return fetch(`/api/finance/aop/prijmy/${id}`, {
                    method: 'PUT', headers, body: JSON.stringify(body),
                });
            }));
            loadData();
        } catch (e) { alert('Chyba při ukládání'); }
        setSaving(false);
    };

    if (loading) return <LayerSpinner />;

    // Souhrny
    const perMesic = (filterFn, mult = 1) => MKEYS.map(k => rows.filter(filterFn).reduce((s, r) => s + num(r[k]) * mult, 0));
    const bezDphM = perMesic(() => true);
    const dph12M  = perMesic(r => Number(r.dph_sazba) === 12, 0.12);
    const dph21M  = perMesic(r => Number(r.dph_sazba) === 21, 0.21);
    const sDphM   = bezDphM.map((b, i) => b + dph12M[i] + dph21M[i]);

    // Roční souhrny = součet měsíců (roční cíl je vždy suma m01..m12)
    const bezDphRok = bezDphM.reduce((a, b) => a + b, 0);
    const dph12Rok  = dph12M.reduce((a, b) => a + b, 0);
    const dph21Rok  = dph21M.reduce((a, b) => a + b, 0);
    const sDphRok   = bezDphRok + dph12Rok + dph21Rok;

    const { stickyBg, headBg } = tableSurfaces(isDark);
    const th = TH;
    const sazbaPill = (sazba) => ({
        fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
        background: sazba === 21 ? 'rgba(139,92,246,0.16)' : 'rgba(16,185,129,0.16)',
        color: sazba === 21 ? VIOLET : OK,
    });

    // sticky styl pro fixní levé sloupce
    const sticky = makeSticky(LEFT, 'cil', siroky ? null : ['kanal']);

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Hlavička vrstvy */}
            <LayerHeader krok={1} title="Příjmy per kanál" cardStyle={cardStyle}>
                Plán {rok} z loňských tržeb: „Generovat z loňska" naplní základ + sezónní tvar, pak u fixních kanálů
                zadáváš <b>jen % navýšení</b> — loňsko, měsíce i roční cíl se dopočítají realtime. Rozvoz / Výdej /
                Bistro / Ubytování jsou fixní, další kanály lze přidat (ty se editují ručně).
            </LayerHeader>

            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-4 flex-wrap flex-shrink-0">
                <div className="inline-flex items-center gap-2 px-2 py-1 rounded-lg" style={{ border: '1px solid var(--border-default)' }}>
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Navýšení</span>
                    <input type="number" step="1" value={defaultProcento}
                        onChange={e => setDefaultProcento(e.target.value)}
                        className="w-12 text-right text-sm rounded px-1.5 py-1 bg-transparent outline-none tabular-nums"
                        style={{ color: 'var(--text-primary)' }} />
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>%</span>
                </div>
                <button onClick={handleGenerovat} disabled={generujici}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
                    {generujici ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    Generovat z loňska
                </button>
                <button onClick={handlePridatKanal}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
                    <Plus size={14} /> Přidat kanál
                </button>
                <div className="flex-1" />
                <BtnUlozit onClick={handleUlozit} saving={saving} dirtyCount={dirty.size} />
            </div>

            {/* Tabule příjmů */}
            <AopTable cardStyle={cardStyle}>
                        <colgroup>
                            <col style={{ width: W.kanal }} />
                            <col style={{ width: W.sazba }} />
                            <col style={{ width: W.lonsko }} />
                            <col style={{ width: W.proc }} />
                            <col style={{ width: W.cil }} />
                            {MKEYS.map((k) => <col key={k} style={{ width: W.mesic }} />)}
                            <col style={{ width: W.koment }} />
                            <col style={{ width: W.akce }} />
                        </colgroup>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                <th className={`${th} text-left`} style={{ ...sticky('kanal', 30, headBg), color: 'var(--text-secondary)' }}>Kanál</th>
                                <th className={`${th} text-left`} style={{ ...sticky('sazba', 30, headBg), color: 'var(--text-tertiary)' }}>DPH</th>
                                <th className={`${th} text-right`} style={{ ...sticky('lonsko', 30, headBg), color: 'var(--text-tertiary)' }}>Loňsko</th>
                                <th className={`${th} text-right`} style={{ ...sticky('proc', 30, headBg), color: 'var(--text-tertiary)' }}>%</th>
                                <th className={`${th} text-right`} style={{ ...sticky('cil', 30, headBg), color: ACCENT }}>Roční cíl</th>
                                {MESICE_SHORT.map((m, i) => (
                                    <th key={i} className={`${th} text-right`} style={{ color: 'var(--text-tertiary)', background: headBg }}>{m}</th>
                                ))}
                                <th className={`${th} text-left`} style={{ color: 'var(--text-tertiary)', background: headBg }}>Komentář</th>
                                <th className={th} style={{ background: headBg }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => {
                                const prev = rows[idx - 1];
                                const novaSkupina = !prev || prev.kanal_kod !== r.kanal_kod || !r.je_fixni;
                                const sumMesice = MKEYS.reduce((s, k) => s + num(r[k]), 0);
                                const topB = novaSkupina ? '1px solid var(--border-default)' : '1px solid transparent';
                                return (
                                    <tr key={r.id}>
                                        {/* Kanál */}
                                        <td className="px-3 py-1.5" style={{ ...sticky('kanal', 10, stickyBg), borderTop: topB }}>
                                            {r.je_fixni ? (
                                                <span className="inline-flex items-center gap-1.5 text-[15px] font-medium" style={{ color: novaSkupina ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                                    <Lock size={11} style={{ color: 'var(--text-tertiary)', opacity: novaSkupina ? 1 : 0 }} />
                                                    {novaSkupina ? r.nazev : ''}
                                                </span>
                                            ) : (
                                                <input value={r.nazev}
                                                    onChange={e => updateCell(r.id, 'nazev', e.target.value)}
                                                    className="w-full bg-transparent border-0 outline-none text-[15px] px-1 py-1.5 rounded-md focus:ring-1 focus:ring-blue-500/60 focus:bg-blue-500/10"
                                                    style={{ color: 'var(--text-primary)' }} />
                                            )}
                                        </td>
                                        {/* DPH sazba */}
                                        <td className="px-2 py-1.5" style={{ ...sticky('sazba', 10, stickyBg), borderTop: topB }}>
                                            {r.je_fixni ? (
                                                <span style={sazbaPill(r.dph_sazba)}>{r.dph_sazba}%</span>
                                            ) : (
                                                <Dropdown value={r.dph_sazba} onChange={v => updateCell(r.id, 'dph_sazba', Number(v))}
                                                    options={[{ value: 12, label: '12 %' }, { value: 21, label: '21 %' }]}
                                                    isDark={isDark} accent="#3B82F6" height={28} />
                                            )}
                                        </td>
                                        {/* Loňsko — u fixních kanálů read-only (z dat, mění jen generátor) */}
                                        <td style={{ ...sticky('lonsko', 10, stickyBg), borderTop: topB }}>
                                            {r.je_fixni ? (
                                                <div className="text-right text-[14px] font-mono tabular-nums px-2 py-2" style={{ color: 'var(--text-tertiary)' }}>{fmtKc(r.zaklad_loni)}</div>
                                            ) : (
                                                <NumCell value={r.zaklad_loni} onChange={v => updateCell(r.id, 'zaklad_loni', v)} color="var(--text-tertiary)" kc sm />
                                            )}
                                        </td>
                                        {/* % */}
                                        <td style={{ ...sticky('proc', 10, stickyBg), borderTop: topB }}>
                                            <input type="text" inputMode="decimal" value={r.procento_navyseni ?? 0}
                                                onChange={e => updateCell(r.id, 'procento_navyseni', e.target.value.replace(/[^\d.-]/g, ''))}
                                                className={CELL} style={{ color: 'var(--text-secondary)' }} />
                                        </td>
                                        {/* Roční cíl = součet měsíců (read-only) */}
                                        <td className="px-2 py-2 text-right text-[14px] font-mono font-semibold tabular-nums"
                                            style={{ ...sticky('cil', 10, stickyBg), borderTop: topB, color: 'var(--text-primary)' }}
                                            title="Roční cíl = součet měsíců">
                                            {fmtKc(sumMesice)}
                                        </td>
                                        {/* Měsíce — editovatelné; ruční úprava měsíc zamkne (generátor ani % přepočet ho nepřepíše) */}
                                        {MKEYS.map((k, idx) => {
                                            const mnum = idx + 1;
                                            const locked = zamSet(r).includes(mnum);
                                            return (
                                            <td key={k} style={{ borderTop: topB, position: 'relative', background: locked ? 'rgba(245,158,11,0.08)' : undefined }}>
                                                <NumCell value={r[k]} onChange={v => updateCell(r.id, k, v)} kc
                                                    color={locked ? WARN : 'var(--text-primary)'} bold={locked}
                                                    title={locked
                                                        ? 'Ručně zamčeno — generátor ani přepočet z % ho nepřepíše. Klikněte na zámek pro odemčení.'
                                                        : 'Úpravou se měsíc zamkne proti automatickému přepisu.'} sm />
                                                {locked && (
                                                    <button onClick={() => toggleMesicLock(r.id, mnum)} title="Odemknout měsíc (vrátit pod automatiku)"
                                                        style={{ position: 'absolute', top: 3, left: 3, padding: 0, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0 }}>
                                                        <Lock size={10} style={{ color: WARN }} />
                                                    </button>
                                                )}
                                            </td>
                                            );
                                        })}
                                        {/* Komentář */}
                                        <td style={{ borderTop: topB }}>
                                            <input value={r.poznamka || ''}
                                                onChange={e => updateCell(r.id, 'poznamka', e.target.value)}
                                                placeholder="…"
                                                className="w-full bg-transparent border-0 outline-none text-[14px] px-2 py-2 rounded-md focus:ring-1 focus:ring-blue-500/60 focus:bg-blue-500/10"
                                                style={{ color: 'var(--text-secondary)' }} />
                                        </td>
                                        {/* Akce */}
                                        <td className="text-center" style={{ borderTop: topB }}>
                                            {!r.je_fixni && (
                                                <button onClick={() => handleSmazat(r.id)} title="Smazat kanál" className="opacity-60 hover:opacity-100">
                                                    <Trash2 size={14} style={{ color: BAD }} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            {/* Příjmy bez DPH */}
                            <tr style={{ borderTop: '2px solid var(--border-default)' }}>
                                <td className="px-3 py-2.5 text-[15px] font-bold" style={{ ...sticky('kanal', 20, headBg), color: 'var(--text-primary)' }}>Příjmy bez DPH</td>
                                <td style={sticky('sazba', 20, headBg)}></td>
                                <td style={sticky('lonsko', 20, headBg)}></td>
                                <td style={sticky('proc', 20, headBg)}></td>
                                <td className="px-2 py-2.5 text-right text-[14px] font-mono font-bold tabular-nums" style={{ ...sticky('cil', 20, headBg), color: ACCENT }}>{fmtKc(bezDphRok)}</td>
                                {bezDphM.map((s, i) => <td key={i} className="px-2 py-2.5 text-right text-[13px] font-mono tabular-nums" style={{ color: 'var(--text-secondary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td colSpan={2} style={{ background: headBg }}></td>
                            </tr>
                            {/* DPH 12 % */}
                            <tr>
                                <td className="px-3 py-1.5 text-[14px]" style={{ ...sticky('kanal', 20, headBg), color: OK }}>DPH 12 %</td>
                                <td style={sticky('sazba', 20, headBg)}></td>
                                <td style={sticky('lonsko', 20, headBg)}></td>
                                <td style={sticky('proc', 20, headBg)}></td>
                                <td className="px-2 py-1.5 text-right text-[13px] font-mono tabular-nums" style={{ ...sticky('cil', 20, headBg), color: OK }}>{fmtKc(dph12Rok)}</td>
                                {dph12M.map((s, i) => <td key={i} className="px-2 py-1.5 text-right text-[12px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td colSpan={2} style={{ background: headBg }}></td>
                            </tr>
                            {/* DPH 21 % */}
                            <tr>
                                <td className="px-3 py-1.5 text-[14px]" style={{ ...sticky('kanal', 20, headBg), color: VIOLET }}>DPH 21 %</td>
                                <td style={sticky('sazba', 20, headBg)}></td>
                                <td style={sticky('lonsko', 20, headBg)}></td>
                                <td style={sticky('proc', 20, headBg)}></td>
                                <td className="px-2 py-1.5 text-right text-[13px] font-mono tabular-nums" style={{ ...sticky('cil', 20, headBg), color: VIOLET }}>{fmtKc(dph21Rok)}</td>
                                {dph21M.map((s, i) => <td key={i} className="px-2 py-1.5 text-right text-[12px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td colSpan={2} style={{ background: headBg }}></td>
                            </tr>
                            {/* Tržby s DPH */}
                            <tr style={{ borderTop: '1px solid var(--border-default)' }}>
                                <td className="px-3 py-2.5 text-[15px] font-bold" style={{ ...sticky('kanal', 20, headBg), color: 'var(--text-primary)' }}>Tržby s DPH</td>
                                <td style={sticky('sazba', 20, headBg)}></td>
                                <td style={sticky('lonsko', 20, headBg)}></td>
                                <td style={sticky('proc', 20, headBg)}></td>
                                <td className="px-2 py-2.5 text-right text-[14px] font-mono font-bold tabular-nums" style={{ ...sticky('cil', 20, headBg), color: 'var(--text-primary)' }}>{fmtKc(sDphRok)}</td>
                                {sDphM.map((s, i) => <td key={i} className="px-2 py-2.5 text-right text-[13px] font-mono font-semibold tabular-nums" style={{ color: 'var(--text-primary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td colSpan={2} style={{ background: headBg }}></td>
                            </tr>
                        </tfoot>
                    </AopTable>
        </div>
    );
}
